import { Chip, Group, Text } from '@mantine/core';
import { DAYS } from './ScheduleField';

/**
 * Which days of the week something happens on, and nothing else.
 *
 * `ScheduleField` next door answers a bigger question — days AND times AND a season — and using it
 * for "they come Tuesdays and Fridays" would put a start time, an end time and two date pickers in
 * front of somebody who wants to press two chips. The stored shape is different too: a bare array
 * of day numbers on the vendor, not a `{rules: […]}` document.
 *
 * The day numbers come from `ScheduleField` so there is one place in this portal that knows Sunday
 * is 0, and they never reach the screen.
 */
export function WeekdaysField({
  label,
  description,
  value,
  onChange,
  emptyMeans,
  disabled,
}: {
  label: string;
  description?: string;
  value: unknown;
  onChange: (days: number[]) => void;
  /** What no days at all means here. Shown when none are picked, so a blank is never a mystery. */
  emptyMeans?: string;
  disabled?: boolean;
}) {
  const days = Array.isArray(value)
    ? value.filter((d): d is number => typeof d === 'number' && d >= 0 && d <= 6)
    : [];

  return (
    <div>
      <Text size="sm" fw={500}>
        {label}
      </Text>
      {description && (
        <Text size="xs" c="dimmed" mb={6}>
          {description}
        </Text>
      )}
      <Chip.Group
        multiple
        value={days.map(String)}
        onChange={(picked) => onChange(picked.map(Number).sort((a, b) => a - b))}
      >
        <Group gap={6} wrap="wrap">
          {DAYS.map((d) => (
            <Chip key={d.value} value={String(d.value)} size="xs" variant="outline" disabled={disabled}>
              {d.short}
            </Chip>
          ))}
        </Group>
      </Chip.Group>
      {days.length === 0 && emptyMeans && (
        <Text size="xs" c="dimmed" mt={6} fs="italic">
          {emptyMeans}
        </Text>
      )}
    </div>
  );
}

/** "Tuesdays and Fridays" — the sentence, for a table cell or a fact line. */
export function describeWeekdays(value: unknown): string | null {
  const days = Array.isArray(value)
    ? [...new Set(value.filter((d): d is number => typeof d === 'number' && d >= 0 && d <= 6))].sort(
        (a, b) => a - b,
      )
    : [];
  if (days.length === 0) return null;
  if (days.length === 7) return 'Every day';
  const names = days.map((d) => `${LONG[d]}s`);
  if (names.length === 1) return names[0] as string;
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

const LONG = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
